/**
 * SRTReportPDF.js — Vidyagni FSLP · NISH NCAHT
 * ─────────────────────────────────────────────────────────────────────────────
 * Generates rich A4 PDF-quality HTML reports matching the PublicSRTReports
 * detail modal style, extended to cover:
 *
 *   generatePublicSRTReport(record, session)
 *     For PublicSRTReports.jsx — superadmin view of a public registration.
 *     Identical to the existing printReport() but extracted here so it can
 *     be imported and called without the modal.
 *
 *   generateStaffSRTReport(sess, child)
 *     For SchoolReadinessTest.jsx / SRTHistoryPanel — staff-assessed sessions.
 *     Same layout as the public report: child info, overall score, 5-area
 *     narrative, 13 skill clusters, home activities, full 90-item table.
 *
 *   generateStaffSRTConsolidated(sessions, child)
 *     All sessions in one document — trajectory table + full detail per session.
 *
 *   generateQSReport(result, child)
 *     Quick Screener — 50 signals, domain flags, recommendation, activities.
 *
 * All functions:
 *   • Open in a new tab with sticky Download/Print bar
 *   • Print-ready A4, logos embedded via import.meta.url
 *   • No external dependencies
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Logo URLs (resolved at module load so they work in Vite) ─────────────────
import NISHlogo  from '../../assets/images/NISHlogo.png'
import NCAHTlogo from '../../assets/images/NCAHTlogo.png'
import ICMRlogo  from '../../assets/images/ICMRlogo.png'

const _abs = (u) => (u && !/^(https?:|data:)/i.test(u)) ? window.location.origin + u : u
const _nishSrc  = _abs(NISHlogo)
const _ncahtSrc = _abs(NCAHTlogo)
const _icmrSrc  = _abs(ICMRlogo)
// ── Constants ─────────────────────────────────────────────────────────────────
const KOSHAS = [
  { id:'annamaya',    icon:'🏃', label:'Annamaya',    full:'Annamaya Kosha',    subtitle:'Physical & Motor',              color:'#0E8A7A', bg:'#E6F5F3' },
  { id:'pranamaya',   icon:'💛', label:'Pranamaya',   full:'Pranamaya Kosha',   subtitle:'Socio-Emotional',               color:'#C2620A', bg:'#FEF0E6' },
  { id:'manomaya',    icon:'🧠', label:'Manomaya',    full:'Manomaya Kosha',    subtitle:'Cognitive',                     color:'#6E3EA6', bg:'#F3EEF9' },
  { id:'vijnanamaya', icon:'📚', label:'Vijnanamaya', full:'Vijnanamaya Kosha', subtitle:'Language, Literacy & Numeracy', color:'#1A4F7A', bg:'#EBF2FA' },
  { id:'anandamaya',  icon:'🎨', label:'Anandamaya',  full:'Anandamaya Kosha',  subtitle:'Aesthetic & Creative',          color:'#B03060', bg:'#FCE8F0' },
]

const KOSHA_ITEMS = {
  annamaya:    ['an01','an02','an03','an04','an05','an06','an07','an08','an09','an10','an11','an12','an13','an14','an15','an16','an17','an18'],
  pranamaya:   ['pr01','pr02','pr03','pr04','pr05','pr06','pr07','pr08','pr09','pr10','pr11','pr12','pr13','pr14','pr15','pr16','pr17','pr18'],
  manomaya:    ['mn01','mn02','mn03','mn04','mn05','mn06','mn07','mn08','mn09','mn10','mn11','mn12','mn13','mn14','mn15','mn16','mn17','mn18'],
  vijnanamaya: ['vj01','vj02','vj03','vj04','vj05','vj06','vj07','vj08','vj09','vj10','vj11','vj12','vj13','vj14','vj15','vj16','vj17','vj18'],
  anandamaya:  ['aa01','aa02','aa03','aa04','aa05','aa06','aa07','aa08','aa09','aa10','aa11','aa12','aa13','aa14','aa15','aa16','aa17','aa18'],
}

const ITEM_META = {
  an01:{title:'Pencil Grip',       rubric:['No grip','Palmar grasp','Transitional','Tripod/mature']},
  an02:{title:'Shape Trace',       rubric:['Cannot attempt','Attempts with support','Completes with errors','Completes independently']},
  an03:{title:'Big & Small',       rubric:['No response','Points with help','Taps with hesitation','Taps correctly']},
  an04:{title:'Body Parts',        rubric:['No response','Random point','Points with prompt','Points independently']},
  an05:{title:'Jump & Land',       rubric:['Cannot attempt','Steps only','Jumps unsteadily','Balanced landing']},
  an06:{title:'Scissors Use',      rubric:["Cannot hold","Holds, can't cut",'Cuts with deviation','Cuts accurately']},
  an07:{title:'Bead Stringing',    rubric:['No attempt','Drops beads','Threads 1–2','Threads 4+ independently']},
  an08:{title:'Ball Catch',        rubric:['Avoids ball','Misses','Body catch','Clean hand catch']},
  an09:{title:'Balance Walk',      rubric:['Cannot attempt','Both hands held','Occasional steps off','Independent']},
  an10:{title:'Draw a Person',     rubric:['Scribble only','Head or one part','2–3 body parts','Recognisable figure']},
  an11:{title:'Colour Inside',     rubric:['Random marks','Mostly outside','Roughly inside','Stays inside']},
  an12:{title:'Spatial Positions', rubric:['No response','Confuses all','1 correct with help','All positions correctly']},
  an13:{title:'Self-Care: Buttons',rubric:['No attempt','Tries, fails','With assistance','Independently']},
  an14:{title:'Right & Left',      rubric:['No response','Confuses both','Identifies with prompt','Identifies independently']},
  an15:{title:'Throw & Aim',       rubric:['No attempt','Random throw','Aimed throw','On-target']},
  an16:{title:'Copy Shapes',       rubric:['Scribbles only','One closed shape','Recognisable circle OR square','Both shapes clearly']},
  an17:{title:'Clap Rhythm',       rubric:['No response','Random clap','Approximates','Exact match']},
  an18:{title:'Handwashing Steps', rubric:['No attempt','1–2 steps','3–4 steps','Full sequence']},
  pr01:{title:'Happy / Sad',       rubric:['No response','With full prompt','With minimal prompt','Names and explains']},
  pr02:{title:'Turn Taking',       rubric:['Cannot wait','Continuous reminders','One reminder','Waits independently']},
  pr03:{title:'Fear & Surprise',   rubric:['No response','Random tap','Identifies with prompt','Identifies independently']},
  pr04:{title:'Greet Others',      rubric:['No response','Eye contact only','Responds when prompted','Spontaneous greeting']},
  pr05:{title:'Sharing',           rubric:['Refuses','Only under pressure','Brief hesitation','Shares willingly']},
  pr06:{title:'Calm-Down Strategy',rubric:['No response','With help','One strategy','Two or more']},
  pr07:{title:'Name Emotions',     rubric:['No response','Random','Identifies with support','Identifies and explains']},
  pr08:{title:'Empathy Response',  rubric:['Ignores','Observes only','Seeks adult','Offers comfort']},
  pr09:{title:'Waiting in Line',   rubric:['Leaves queue','3+ reminders','One reminder','Waits independently']},
  pr10:{title:'Express a Need',    rubric:['Cries/grabs','Points/gestures','Single word/sign','Phrase or sentence']},
  pr11:{title:'Follow 2-Step Rule',rubric:['No follow','Step 1 only','With repetition','First time, both steps']},
  pr12:{title:'Approach New Task', rubric:['Refuses with distress','Watches only','Tries with support','Approaches willingly']},
  pr13:{title:'Group Participation',rubric:['Stays isolated','Watches','Partial','Full participation']},
  pr14:{title:'Repair & Forgive',  rubric:['No response','Prompted sorry','Unprompted sorry','Full repair']},
  pr15:{title:'Help Seeking',      rubric:['Freezes/gives up','Waits silently','Signals','Asks verbally']},
  pr16:{title:'Pretend Play',      rubric:['No pretend','Functional use','Single action','Extended scenario']},
  pr17:{title:'Persistence',       rubric:['Gives up','One try','With encouragement','Self-encourages']},
  pr18:{title:'My Favourite Thing',rubric:['No response','Names only','Name + reason','Elaborated preference']},
  mn01:{title:'Odd One Out',       rubric:['No attempt','Full prompt','Minimal prompt','Identifies and explains']},
  mn02:{title:'AB Pattern',        rubric:['No response','Incorrect','Extends with prompt','Extends independently']},
  mn03:{title:'Sort Animals/Vehicles',rubric:['No sorting','1–2 correct','Most correct','All correct']},
  mn04:{title:'3-Item Memory',     rubric:['Recalls none','Recalls 1','Recalls 2','Recalls all 3']},
  mn05:{title:'Cause & Effect',    rubric:['No response','Random','Identifies with support','Identifies and explains']},
  mn06:{title:'ABC Pattern',       rubric:['No response','Incorrect','Correct with prompt','Correct independently']},
  mn07:{title:'Find the Same',     rubric:['No attempt','Finds 1','Both with prompt','Both independently']},
  mn08:{title:'Simple Problem Solving',rubric:['No attempt','Random action','One solution','Multiple strategies']},
  mn09:{title:'Sequence 3 Events', rubric:['Random','1 pair','2 correct','Full sequence']},
  mn10:{title:'Match by Function', rubric:['No response','Random','Matches with prompt','Matches independently']},
  mn11:{title:'What If…?',         rubric:['No response','One word','Short sentence','Elaborated scenario']},
  mn12:{title:'Puzzle Focus',      rubric:['Cannot attend','1–2 pieces','Needs refocus','Completes independently']},
  mn13:{title:'True or False',     rubric:['No response','Guesses','Correct without reasoning','Correct with reasoning']},
  mn14:{title:'Two-Attribute Match',rubric:['No response','Matches one only','Matches both with prompt','Matches both independently']},
  mn15:{title:'3-Step Instruction',rubric:['No follow','1 step','2 steps','All 3 steps']},
  mn16:{title:"What's Missing?",   rubric:['No response','Random guess','With prompt','Independently']},
  mn17:{title:'Big → Small Order', rubric:['Random','Partial','Correct with help','Correct independently']},
  mn18:{title:'Story Retell',      rubric:['No recall','1 element','2–3 elements','Full sequence']},
  vj01:{title:'Picture Description',rubric:['No response','1–2 labels','Partial scene','Full narrative']},
  vj02:{title:'Starting Letter',   rubric:['No response','Full prompt','Minimal prompt','Independently']},
  vj03:{title:'Count Objects',     rubric:['No counting','Counts with full support','Counts with prompt','Counts independently']},
  vj04:{title:'More or Less',      rubric:['No response','Random','Identifies with prompt','Identifies and explains']},
  vj05:{title:'Name 5 Letters',    rubric:['None','1–2','3–4','All 5']},
  vj06:{title:'Rhyming Words',     rubric:['No response','Random','Identifies with prompt','Identifies independently']},
  vj07:{title:'Number Order 1–5',  rubric:['Random','1–2 correct','3–4 correct','All correct']},
  vj08:{title:'Find All Circles',  rubric:['No attempt','1 circle','2 circles','All 3 circles']},
  vj09:{title:'Own Name Reading',  rubric:['No recognition','Guesses','With help','Independently']},
  vj10:{title:'One More',          rubric:['No response','Random','Correct using objects','Correct mentally']},
  vj11:{title:'First Sound',       rubric:['No response','Whole word','Similar sound','Correct /s/']},
  vj12:{title:'Story Comprehension',rubric:['No response','1 answer','2 answers','All 3 answers']},
  vj13:{title:'Blend Sounds',      rubric:['No attempt','Repeats sounds','Approximates','Blends correctly']},
  vj14:{title:'Bigger Number',     rubric:['No response','Random','Correct using number line','Correct mentally']},
  vj15:{title:'Yesterday/Today/Tomorrow',rubric:['No response','Random','Correct with visual','Correct independently']},
  vj16:{title:'Make a Sentence',   rubric:['No attempt','Single word','2-word phrase','Full sentence']},
  vj17:{title:'Ordinal Numbers',   rubric:['No response','Random','Identifies with prompt','Identifies independently']},
  vj18:{title:'Write Own Name',    rubric:['Scribble only','Partial/unrecognisable','Recognisable attempt','Legible, complete']},
  aa01:{title:'Favourite Colour',  rubric:['No response','Points randomly','Points to one colour','Names + personal reason']},
  aa02:{title:'Free Drawing',      rubric:['No mark-making','Random marks','Intentional marks','Recognisable drawing']},
  aa03:{title:'Favourite Story',   rubric:['No engagement','Title only','1–2 elements','Full re-enactment']},
  aa04:{title:'Move to Music',     rubric:['No movement','Minimal','Rhythmic','Full expressive response']},
  aa05:{title:'Favourite Animal',  rubric:['No response','Name only','Name + fact/feeling','Name + emotion + facts']},
  aa06:{title:'Play Dough Creation',rubric:['Avoids','Manipulates','Recognisable form','Form with narrative']},
  aa07:{title:'Sing a Song',       rubric:['No attempt','Hum/mumble','Partial lyrics','Full with expression']},
  aa08:{title:'Festival Knowledge',rubric:['No response','Name only','Name + activity','Full description']},
  aa09:{title:'Colour Mixing',     rubric:['No response','Random','Correct after demo','Correct independently']},
  aa10:{title:'Nature Appreciation',rubric:['No response','Names one','Name + reason','Detailed description']},
  aa11:{title:'Body Shape',        rubric:['No attempt','Random','Approximates','Clear shape']},
  aa12:{title:'Design a Flag',     rubric:['No marks','Random marks','Intentional design','Design + explanation']},
  aa13:{title:'Story Ending',      rubric:['No response','One word','Short phrase','Creative elaborated ending']},
  aa14:{title:'Puppet Play',       rubric:['Passive hold','Moves puppet','Name/voice','Full mini-story']},
  aa15:{title:'Texture Talk',      rubric:['Avoids','Touches, no words','One difference','Both described']},
  aa16:{title:'Joyful Memory',     rubric:['No response','Event named','Event + reason','Full emotional narrative']},
  aa17:{title:'Traditional Art',   rubric:['No response','Names','Describes','Demonstrates']},
  aa18:{title:'Dream Invention',   rubric:['No response','Single word','Brief description','Elaborated with purpose']},
}

const LEVEL_SCORES  = { 'not-attempted':0, 'emerging':1, 'developing':2, 'achieved':3 }
const LEVEL_LABELS  = { 'not-attempted':'Not Assessed', 'emerging':'Emerging', 'developing':'Developing', 'achieved':'Achieved' }
const LEVEL_COLORS  = { 'not-attempted':'#6B7280', 'emerging':'#DC2626', 'developing':'#D97706', 'achieved':'#16A34A' }
const LEVEL_BGS     = { 'not-attempted':'#F3F4F6', 'emerging':'#FEE2E2', 'developing':'#FEF3C7', 'achieved':'#DCFCE7' }

const SKILL_CLUSTERS = [
  { id:'gross_motor',      label:'Gross Motor Skills',          icon:'🏃', color:'#0E8A7A', bg:'#E6F5F3', items:['an05','an08','an09','an15','an17','an04'] },
  { id:'fine_motor',       label:'Fine Motor Skills',           icon:'✏️', color:'#1A4F7A', bg:'#EBF2FA', items:['an01','an02','an06','an07','an10','an11','an16','vj18'] },
  { id:'sensory_body',     label:'Sensory & Body Awareness',    icon:'👁️', color:'#6E3EA6', bg:'#F3EEF9', items:['an03','an04','an12','an14','aa15'] },
  { id:'self_care',        label:'Self-Care & Daily Living',    icon:'🙌', color:'#B45309', bg:'#FEF3C7', items:['an13','an18','pr06','pr10','pr15'] },
  { id:'socio_emotional',  label:'Socio-Emotional Development', icon:'💛', color:'#C2620A', bg:'#FEF0E6', items:['pr01','pr03','pr04','pr05','pr07','pr08','pr13','pr14','pr17','pr18'] },
  { id:'attention_memory', label:'Attention & Memory',          icon:'🧩', color:'#6E3EA6', bg:'#F3EEF9', items:['mn04','mn12','mn16','pr11','mn15'] },
  { id:'cognitive',        label:'Cognitive & Reasoning',       icon:'🧠', color:'#5D4037', bg:'#EFEBE9', items:['mn01','mn02','mn05','mn06','mn07','mn08','mn09','mn10','mn13','mn14','mn17','mn18'] },
  { id:'language',         label:'Language & Communication',    icon:'🗣️', color:'#1A4F7A', bg:'#EBF2FA', items:['vj01','vj12','vj16','pr10','mn11','mn18','aa03'] },
  { id:'literacy',         label:'Emergent Literacy',           icon:'📖', color:'#0D5C63', bg:'#E8F8F5', items:['vj02','vj05','vj06','vj09','vj11','vj13','vj18'] },
  { id:'numeracy',         label:'Early Numeracy',              icon:'🔢', color:'#B45309', bg:'#FFFBEB', items:['vj03','vj04','vj07','vj08','vj10','vj14','vj17','mn17'] },
  { id:'creative',         label:'Creative Expression',         icon:'🎨', color:'#B03060', bg:'#FCE8F0', items:['aa02','aa06','aa07','aa12','aa13','aa14','aa17','mn11'] },
  { id:'aesthetic',        label:'Aesthetic & Cultural',        icon:'🏺', color:'#7C3AED', bg:'#EDE9FE', items:['aa04','aa08','aa09','aa10','aa15','aa16','aa17'] },
  { id:'play_imagination', label:'Play & Imagination',          icon:'🎭', color:'#0891B2', bg:'#ECFEFF', items:['pr16','aa13','aa14','aa18','mn11','pr12'] },
]

const CLUSTER_ACTIVITIES = {
  gross_motor:      [{title:'Hopscotch & Jump Squares',desc:'Draw numbered squares with chalk. Have the child hop on one foot, two feet, or jump over gaps. Builds leg strength, balance, and spatial awareness.'},{title:'Balloon Catch & Throw',desc:'Toss a balloon back and forth. Balloons move slowly, giving the child time to track and catch. Gradually switch to a soft foam ball.'},{title:'Copy-the-Clap Game',desc:'Tap out simple rhythms — 2 claps, pause, 1 clap — and ask the child to copy exactly. Build up to 4-beat patterns.'}],
  fine_motor:       [{title:'Tearing & Sticking Collage',desc:'Have the child tear newspaper into small pieces and paste them to make a picture. Develops pincer grip and bilateral hand coordination.'},{title:'Playdough Shapes & Snakes',desc:'Roll playdough into snakes, balls, and flat circles. Builds hand strength and pressure control.'},{title:'Scissor Practice Strip',desc:'Draw bold straight lines on strips of paper. Have the child cut along the lines with child-safe scissors. Progress from straight to wavy to zigzag.'}],
  sensory_body:     [{title:'Body Part Freeze Dance',desc:'Play music and call out a body part when the music stops — "Touch your elbow!", "Stand on your right foot!" Makes body schema fun.'},{title:'Treasure Hunt with Position Words',desc:"Hide a toy and give clues using prepositions: \"It's under the chair\", \"Look behind the cushion\"."},{title:'Texture Feely Box',desc:'Put objects of different textures in a box. The child touches one and describes it before pulling it out.'}],
  self_care:        [{title:'Button & Zip Board',desc:"Make a dressing frame with 4–5 different fasteners. Practise daily for 5 minutes with no time pressure."},{title:'Handwashing Song Routine',desc:'Use the Happy Birthday song as a timer. Make a picture-card sequence of the 6 steps posted by the sink.'},{title:'"I Need…" Visual Board',desc:'Create a card with pictures: water, toilet, help, break, quiet. Teach the child to point and say the word.'}],
  socio_emotional:  [{title:'Emotion Mirror Game',desc:'Sit facing each other with a small mirror. Make a face and ask the child to copy it, then name it.'},{title:'Feelings Wheel Diary',desc:'Each evening, point to a feelings wheel and ask the child to pick how they felt today and tell one thing that caused it.'},{title:'Cooperative Building Game',desc:'Use blocks where each person can only place one block per turn. Requires waiting, sharing, and persistence all at once.'}],
  attention_memory: [{title:'Shopping List Game',desc:"Say \"remember to get: apple, ball, and shoe.\" Walk to another room to get the items. Start with 2 items, build to 4."},{title:"What's Missing? Tray",desc:'Put 5 objects on a tray. Cover it and secretly remove one. Reveal and ask "What\'s missing?"'},{title:'Simon Says 3-Step',desc:'"Simon says: clap your hands, then touch your nose, then sit down." Give all three instructions before the child moves.'}],
  cognitive:        [{title:'Pattern Stamps & Claps',desc:'Make a pattern with stamps: red-blue-red-blue. Ask the child to continue it. Pair with clapping.'},{title:'Story Sequencing Cards',desc:'Draw 3-picture sequences of familiar events. Cut them up and ask the child to put them in order.'},{title:'Sorting Treasure Hunt',desc:'Mix buttons, coins, and pasta. Ask the child to sort by one rule (shape), then re-sort by another (size).'}],
  language:         [{title:'Picture Talk Time',desc:'Choose a detailed picture book page. Take turns pointing to things and describing them in full sentences.'},{title:'Bedtime Story Questions',desc:'After reading, ask: "What happened first? What did the character feel? What would you do?"'},{title:'What If? Question Jar',desc:'Write fun "What if" questions on slips of paper. Pull one at dinner and each person answers in a full sentence.'}],
  literacy:         [{title:'Rhyme Time Every Day',desc:'Say a word and ask the child to say a word that rhymes — "cat… bat… hat…" Use nonsense words too.'},{title:'Name Letter Hunt',desc:"Write the child's name on a card. Give them a magazine and ask them to circle every letter from their name."},{title:'Sound Robot Game',desc:'"c-a-t… what\'s that word?" The child blends it. Then reverse — the child breaks words into robot sounds.'}],
  numeracy:         [{title:'Muffin Tin Counting',desc:'Put numbers 1–6 in the cups of a muffin tin. The child drops the correct number of objects (buttons, raisins) into each cup.'},{title:'More & Fewer Snack Game',desc:'Give two plates with different amounts. Ask "Which plate has more?" They eat from the bigger plate as a reward.'},{title:'Staircase of Sizes',desc:'Collect 5 objects of different sizes. Line them up from smallest to biggest, then say "first… second… third…"'}],
  creative:         [{title:'Story Bag',desc:'Put 4 unrelated objects in a bag. The child picks them out one by one and must include each in a story.'},{title:'Mini Art Gallery',desc:'Give the child any media — crayons, watercolour, torn paper. After finishing, ask "Tell me about your picture."'},{title:'Karaoke Humming',desc:'Play familiar songs and encourage singing along — even humming counts. Progress to filling in the last word of each line.'}],
  aesthetic:        [{title:'Kitchen Colour Mixing',desc:'Mix food colouring drops into water: red + blue, yellow + blue. Ask the child to predict the colour first.'},{title:'Nature Walk & Wonder Journal',desc:'On a walk, ask the child to find one beautiful thing, one rough thing, one that smells interesting.'},{title:'Family Festival Box',desc:'Create a box of items from one family festival. The child presents it: what is the festival, what do we do.'}],
  play_imagination: [{title:'Loose Parts Play Shelf',desc:'Dedicate a shelf to open-ended materials: cardboard tubes, fabric scraps, bottle caps. No instructions — the child decides what they become.'},{title:'Puppet Conversation',desc:'Have two puppets meet and one tells a problem. Ask the child to make the second puppet respond.'},{title:'Yes-And Storytelling',desc:'"Once there was a cat who found a key." Child adds one sentence. You add one. Keep going for 10 turns.'}],
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function levelFor(pct) {
  if (pct >= 75) return { label:'Achieved',      color:'#16A34A', bg:'#DCFCE7' }
  if (pct >= 45) return { label:'Developing',    color:'#D97706', bg:'#FEF3C7' }
  if (pct >   0) return { label:'Emerging',      color:'#DC2626', bg:'#FEE2E2' }
  return               { label:'Not Attempted', color:'#6B7280', bg:'#F3F4F6' }
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' }) : '—'
}
function ageSafe(dob) {
  if (!dob) return '—'
  return Math.floor((new Date() - new Date(dob)) / 31557600000) + ' years'
}
function isAttempted(resp) {
  return resp && (resp.interacted === true || (resp.level && resp.level !== 'not-attempted'))
}

function levelBadge(level) {
  const color = LEVEL_COLORS[level] || '#6B7280'
  const bg    = LEVEL_BGS[level]   || '#F3F4F6'
  const label = LEVEL_LABELS[level] || 'Not Assessed'
  return `<span style="display:inline-block;background:${bg};color:${color};font-size:11px;font-weight:800;padding:3px 10px;border-radius:5px;border:1px solid ${color}55">${label}</span>`
}

function clusterScore(cluster, responses) {
  const att = cluster.items.filter(id => isAttempted(responses[id]))
  if (!att.length) return null
  const sum = att.reduce((s, id) => s + (LEVEL_SCORES[responses[id].level] || 0), 0)
  return Math.round((sum / (att.length * 3)) * 100)
}

function openReport(html) {
  const win = window.open('', '_blank')
  if (!win) { alert('Please allow pop-ups to open this report.'); return }
  win.document.write(html)
  win.document.close()
}

// ── Shared HTML blocks ────────────────────────────────────────────────────────
function printBar(title) {
  return `<div class="no-print" style="background:#0F1A2E;color:white;padding:12px 24px;
    display:flex;justify-content:space-between;align-items:center;position:sticky;top:0;z-index:100;font-family:'Segoe UI',Arial,sans-serif">
    <span style="font-weight:700;font-size:13px">📋 ${title}</span>
    <div style="display:flex;gap:10px">
      <button onclick="window.print()" style="background:#F5D000;color:#0F1A2E;border:none;padding:8px 20px;border-radius:7px;font-weight:800;font-size:12px;cursor:pointer">
        🖨️ Print / Save as PDF</button>
      <button onclick="window.close()" style="background:rgba(255,255,255,.15);color:white;border:1px solid rgba(255,255,255,.3);padding:8px 14px;border-radius:7px;font-size:12px;cursor:pointer">
        ✕ Close</button>
    </div>
  </div>`
}

function pageCSS() {
  return `<style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'Segoe UI',Arial,sans-serif;font-size:14px;color:#111827;background:white}
    .page{padding:24px 28px;max-width:900px;margin:0 auto}
    h2{font-size:18px;font-weight:800;color:#0F1A2E;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #E5E7EB}
    p{line-height:1.75}
    .section{margin-bottom:28px}
    .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
    .info-card{background:#F8FAFC;border:1px solid #E5E7EB;border-radius:8px;padding:10px 14px}
    .info-card .label{font-size:11px;color:#6B7280;font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px}
    .info-card .value{font-size:14px;font-weight:700;color:#111827;word-break:break-all}
    .cluster-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:20px}
    .strengths-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:20px}
    .sig-block{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:20px}
    .sig-line{border-bottom:1px solid #9CA3AF;padding-bottom:4px;font-size:13px;color:#6B7280;margin-bottom:4px}
    .disclaimer{background:#FEF3C7;border:1.5px solid #FCD34D;border-radius:8px;padding:12px 16px;font-size:13px;color:#78350F;line-height:1.65}
    .no-print{display:block}
    @media print{
      @page{size:A4;margin:10mm}
      .no-print{display:none!important}
      .page-break{page-break-before:always}
    }
  </style>`
}

function nishHeader(subtitle, reportDate) {
  const logos = (_nishSrc || _ncahtSrc || _icmrSrc)
    ? `<div style="display:flex;align-items:center;gap:12px">
        ${_nishSrc  ? `<img src="${_nishSrc}"  alt="NISH"  height="52" style="object-fit:contain"/>` : '<div style="background:#0F1A2E;color:white;font-weight:900;font-size:14px;padding:6px 10px;border-radius:4px">NISH</div>'}
        ${_ncahtSrc ? `<img src="${_ncahtSrc}" alt="NCAHT" height="52" style="object-fit:contain"/>` : '<div style="background:#b30000;color:white;font-size:10px;font-weight:700;padding:4px 8px;border-radius:4px">NCAHT</div>'}
        ${_icmrSrc  ? `<img src="${_icmrSrc}"  alt="ICMR"  height="52" style="object-fit:contain"/>` : '<div style="background:#006400;color:white;font-size:10px;font-weight:700;padding:4px 8px;border-radius:4px">ICMR</div>'}
      </div>`
    : `<div style="display:flex;gap:6px">
        <div style="background:#0F1A2E;color:white;font-weight:900;font-size:14px;padding:6px 10px;border-radius:4px">NISH</div>
        <div style="background:#b30000;color:white;font-size:10px;font-weight:700;padding:5px 8px;border-radius:4px">NCAHT</div>
        <div style="background:#006400;color:white;font-size:10px;font-weight:700;padding:5px 8px;border-radius:4px">ICMR</div>
      </div>`
  return `<div style="border-bottom:3px solid #1A4F7A;padding-bottom:14px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:center">
    ${logos}
    <div style="text-align:center">
      <div style="font-size:18px;font-weight:900;color:#0F1A2E">${subtitle}</div>
      <div style="font-size:12px;color:#6B7280;margin-top:3px">Panchakosha Assessment · 90 Skills · 5 Developmental Areas</div>
      <div style="font-size:11px;color:#9CA3AF;margin-top:2px">NCF-FS 2022 · NEP 2020 · Play-Based · NISH Vidyagni Platform</div>
    </div>
    <div style="text-align:right;font-size:11px;color:#9CA3AF">
      <div>Report Date</div>
      <div style="font-weight:700;color:#1F2937">${reportDate}</div>
    </div>
  </div>`
}

function overallScoreBlock(childName, overall) {
  const lv = levelFor(overall)
  const narrative = overall >= 75
    ? `<strong>${childName}</strong> is showing strong readiness across all five developmental areas. Most skills were at the <em>Achieved</em> level — this child is well-positioned for the foundational stage of schooling.`
    : overall >= 45
    ? `<strong>${childName}</strong> is making good progress. Several skills are developing well, and some areas would benefit from extra support and structured play activities.`
    : overall > 0
    ? `<strong>${childName}</strong> is at an early stage of development in several areas. A follow-up visit to NISH is strongly recommended for a detailed clinical evaluation and personalised support plan.`
    : `No items have been rated yet for <strong>${childName}</strong>. Complete the full 90-item assessment to generate a developmental profile.`
  return `<div style="background:#0F1A2E;color:white;border-radius:12px;padding:20px 24px;display:flex;align-items:center;gap:24px;margin-bottom:22px">
    <div>
      <div style="font-size:12px;color:rgba(255,255,255,.5);font-weight:700;letter-spacing:.8px;margin-bottom:6px">OVERALL READINESS SCORE</div>
      <div style="font-size:56px;font-weight:900;line-height:1;color:${lv.color}">${overall}%</div>
      <div style="display:inline-block;background:${lv.bg};color:${lv.color};font-weight:800;font-size:14px;padding:4px 16px;border-radius:8px;margin-top:8px">${lv.label}</div>
    </div>
    <div style="flex:1">
      <div style="font-size:12px;color:rgba(255,255,255,.5);font-weight:700;letter-spacing:.6px;margin-bottom:10px">WHAT THIS MEANS</div>
      <p style="font-size:14px;color:rgba(255,255,255,.8);line-height:1.7">${narrative}</p>
    </div>
  </div>`
}

function koshaBarSection(childName, koshaScores, responses) {
  const rows = KOSHAS.map(k => {
    const pct      = koshaScores[k.id] ?? 0
    const klv      = levelFor(pct)
    const attempted = (KOSHA_ITEMS[k.id]||[]).filter(id => isAttempted(responses[id])).length
    return `<div style="margin-bottom:14px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:5px">
        <span style="font-size:15px;font-weight:700;color:${k.color}">${k.icon} ${k.full}</span>
        <div style="display:flex;align-items:center;gap:8px">
          <span style="font-size:11px;color:#9CA3AF">${attempted}/18 skills checked</span>
          <span style="background:${klv.bg};color:${klv.color};font-size:11px;font-weight:700;padding:3px 10px;border-radius:6px">${klv.label}</span>
          <span style="font-size:18px;font-weight:900;color:${klv.color};min-width:44px;text-align:right">${pct}%</span>
        </div>
      </div>
      <div style="height:12px;background:#E5E7EB;border-radius:6px;overflow:hidden">
        <div style="width:${pct}%;height:100%;background:${k.color};border-radius:6px"></div>
      </div>
    </div>`
  }).join('')

  const narratives = KOSHAS.map(k => {
    const pct = koshaScores[k.id] ?? 0
    const klv = levelFor(pct)
    const text = pct === 0
      ? `We did not assess the ${k.full} area during this session. A follow-up assessment is recommended.`
      : pct >= 75
      ? `${childName} showed strong skills in the ${k.full} area. Most activities were completed independently and confidently.`
      : pct >= 45
      ? `${childName} is making good progress in the ${k.full} area. Some activities were done well, while others need more practice. With the right support, further progress is expected.`
      : `${childName} is at an early stage in the ${k.full} area. Several activities were difficult or could not be attempted. Focused support and play-based activities are recommended.`
    return `<div style="background:#FAFBFC;border-radius:10px;padding:14px 16px;margin-bottom:12px;border-left:4px solid ${k.color}">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
        <span style="font-size:20px">${k.icon}</span>
        <span style="font-size:15px;font-weight:800;color:${k.color}">${k.full}</span>
        <span style="background:${klv.bg};color:${klv.color};font-size:11px;font-weight:700;padding:2px 9px;border-radius:6px;margin-left:auto">${pct}% · ${klv.label}</span>
      </div>
      <p style="font-size:14px;color:#374151;line-height:1.75">${text}</p>
    </div>`
  }).join('')

  return { rows, narratives }
}

function itemTableSection(responses, koshaScores) {
  return KOSHAS.map(k => {
    const kPct = koshaScores[k.id] ?? 0
    const kLv  = levelFor(kPct)
    const rows = (KOSHA_ITEMS[k.id] || []).map(itemId => {
      const meta        = ITEM_META[itemId]
      const resp        = responses[itemId]
      const level       = resp?.level || 'not-attempted'
      const rubricLabel = meta?.rubric?.[LEVEL_SCORES[level]] || LEVEL_LABELS[level]
      const attempted   = isAttempted(resp)
      return `<tr style="border-bottom:1px solid #E5E7EB;background:${attempted?'white':'#FAFAFA'}">
        <td style="padding:8px 12px;font-size:13px;font-weight:700;color:#111827;width:175px">${meta?.title || itemId}</td>
        <td style="padding:8px 12px;width:115px">${attempted ? levelBadge(level) : '<span style="font-size:11px;color:#9CA3AF;font-style:italic">Not assessed</span>'}</td>
        <td style="padding:8px 12px;font-size:13px;color:#374151;font-weight:500">${attempted ? rubricLabel : '—'}</td>
        <td style="padding:8px 12px;font-size:12px;color:#6B7280;font-style:italic">${resp?.note || ''}</td>
      </tr>`
    }).join('')
    return `<div style="margin-bottom:28px;page-break-inside:avoid">
      <div style="background:${k.color};color:white;padding:10px 16px;border-radius:8px 8px 0 0;display:flex;justify-content:space-between;align-items:center">
        <span style="font-size:16px;font-weight:800">${k.icon} ${k.full}</span>
        <span style="font-size:14px;font-weight:700;background:rgba(255,255,255,.2);padding:3px 12px;border-radius:20px">${kPct}% · ${kLv.label}</span>
      </div>
      <table style="width:100%;border-collapse:collapse;border:1px solid #E5E7EB;border-top:none">
        <thead><tr style="background:#F8FAFC">
          <th style="padding:8px 12px;text-align:left;font-size:11px;color:#6B7280;font-weight:700;text-transform:uppercase;width:175px">Skill</th>
          <th style="padding:8px 12px;text-align:left;font-size:11px;color:#6B7280;font-weight:700;text-transform:uppercase;width:115px">Level</th>
          <th style="padding:8px 12px;text-align:left;font-size:11px;color:#6B7280;font-weight:700;text-transform:uppercase">What the child did</th>
          <th style="padding:8px 12px;text-align:left;font-size:11px;color:#6B7280;font-weight:700;text-transform:uppercase">Assessor Note</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`
  }).join('')
}
// Lightweight cluster→kosha mapping for Quick Screener preview — there's no
// item-level data from a 50-signal screening, so this only shows which kosha
// each cluster broadly belongs to, never a fabricated per-cluster score.
const CLUSTER_KOSHA = {
  gross_motor: 'annamaya', fine_motor: 'annamaya', sensory_body: 'annamaya',
  self_care: 'pranamaya', socio_emotional: 'pranamaya',
  attention_memory: 'manomaya', cognitive: 'manomaya',
  language: 'vijnanamaya', literacy: 'vijnanamaya', numeracy: 'vijnanamaya',
  creative: 'anandamaya', aesthetic: 'anandamaya', play_imagination: 'anandamaya',
}

function qsClusterPreviewHTML(domainScores, flags) {
  const tiles = SKILL_CLUSTERS.map(c => {
    const koshaId     = CLUSTER_KOSHA[c.id]
    const kosha       = KOSHAS.find(k => k.id === koshaId)
    const notAssessed = (flags?.[koshaId] === 'none') || domainScores[koshaId] == null
    const pct         = notAssessed ? null : domainScores[koshaId]
    return `<div style="border:1.5px solid #E5E7EB;border-radius:8px;padding:9px 11px;background:white">
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
        <span style="font-size:14px">${c.icon}</span>
        <span style="font-size:11px;font-weight:700;color:#111827">${c.label}</span>
      </div>
      ${kosha ? `<span style="font-size:10px;font-weight:700;color:${kosha.color};background:${kosha.bg};padding:2px 7px;border-radius:5px">≈ ${kosha.icon} ${kosha.label}${pct!=null?` · ${pct}%`:''}</span>` : ''}
    </div>`
  }).join('')

  return `<div style="background:#FEF3C7;border:2px solid #FCD34D;border-radius:10px;padding:16px 18px;margin-bottom:16px;display:flex;gap:12px;align-items:flex-start">
      <span style="font-size:24px;flex-shrink:0">🔒</span>
      <div>
        <div style="font-size:13px;font-weight:800;color:#92400E;margin-bottom:4px">Detailed Skill Cluster Analysis isn't available yet</div>
        <p style="font-size:12px;color:#374151;line-height:1.7;margin:0">The 13 functional clusters need item-level data from the full 90-item Panchakosha SRT to score accurately. A Quick Screener only gives a kosha-level signal, so the outline below is a rough preview — not a real cluster score.</p>
        <p style="font-size:12px;font-weight:700;color:#92400E;margin:8px 0 0">Full cluster analysis will be available once the full 90-item SRT is completed — including per-cluster scores, clinical observations, and home activities.</p>
      </div>
    </div>
    <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px">${tiles}</div>`
}

function clusterSection(responses) {
  const withScores = SKILL_CLUSTERS.map(c => ({ ...c, pct: clusterScore(c, responses) }))
  const strengths  = withScores.filter(c => c.pct !== null && c.pct >= 75)
  const developing = withScores.filter(c => c.pct !== null && c.pct >= 45 && c.pct < 75)
  const needs      = withScores.filter(c => c.pct !== null && c.pct < 45)

  const clusterGrid = withScores.map(c => {
    const pct      = c.pct
    const attempted = c.items.filter(id => isAttempted(responses[id])).length
    const clv      = pct !== null ? levelFor(pct) : { label:'Not Assessed', color:'#9CA3AF', bg:'#F3F4F6' }
    const bar      = pct !== null
      ? `<div style="height:8px;background:#E5E7EB;border-radius:4px;margin:6px 0 4px;overflow:hidden">
          <div style="width:${pct}%;height:100%;background:${c.color};border-radius:4px"></div>
         </div>` : ''
    return `<div style="border:1.5px solid #E5E7EB;border-radius:10px;overflow:hidden${pct===null?';opacity:.5':''}">
      <div style="height:4px;background:${pct!==null?c.color:'#E5E7EB'}"></div>
      <div style="padding:10px 12px">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:4px">
          <span style="font-size:13px;font-weight:800;color:${c.color}">${c.icon} ${c.label}</span>
          <span style="font-size:16px;font-weight:900;color:${clv.color}">${pct!==null?pct+'%':'—'}</span>
        </div>
        ${bar}
        <div style="display:flex;justify-content:space-between;align-items:center;margin-top:4px">
          <span style="background:${clv.bg};color:${clv.color};font-size:10px;font-weight:700;padding:2px 8px;border-radius:4px">${clv.label}</span>
          <span style="font-size:10px;color:#9CA3AF">${attempted}/${c.items.length} skills</span>
        </div>
      </div>
    </div>`
  }).join('')

  const strengthsGrid = `
    <div style="background:#DCFCE7;border-radius:10px;padding:14px 16px;border:1.5px solid #16A34A44">
      <div style="font-size:13px;font-weight:800;color:#15803D;margin-bottom:10px">✅ Areas of Strength (${strengths.length})</div>
      ${strengths.length===0?'<p style="font-size:13px;color:#6B7280;font-style:italic">None at Achieved level yet</p>':strengths.map(c=>`<div style="font-size:13px;color:#166534;font-weight:600;margin-bottom:5px">${c.icon} ${c.label} <span style="color:#16A34A">(${c.pct}%)</span></div>`).join('')}
    </div>
    <div style="background:#FEF3C7;border-radius:10px;padding:14px 16px;border:1.5px solid #D97706aa">
      <div style="font-size:13px;font-weight:800;color:#B45309;margin-bottom:10px">🔄 Still Developing (${developing.length})</div>
      ${developing.length===0?'<p style="font-size:13px;color:#6B7280;font-style:italic">None in this range</p>':developing.map(c=>`<div style="font-size:13px;color:#92400E;font-weight:600;margin-bottom:5px">${c.icon} ${c.label} <span style="color:#D97706">(${c.pct}%)</span></div>`).join('')}
    </div>
    <div style="background:#FEE2E2;border-radius:10px;padding:14px 16px;border:1.5px solid #DC262644">
      <div style="font-size:13px;font-weight:800;color:#B91C1C;margin-bottom:10px">⚠️ Needs Support (${needs.length})</div>
      ${needs.length===0?'<p style="font-size:13px;color:#6B7280;font-style:italic">No areas at Emerging level</p>':needs.map(c=>`<div style="font-size:13px;color:#991B1B;font-weight:600;margin-bottom:5px">${c.icon} ${c.label} <span style="color:#DC2626">(${c.pct}%)</span></div>`).join('')}
    </div>`

  return { clusterGrid, strengthsGrid, topNeeds: [...withScores.filter(c=>c.pct!==null)].sort((a,b)=>a.pct-b.pct).slice(0,3) }
}

function homeActivitiesHTML(topNeeds) {
  return topNeeds.map(c => {
    const acts = CLUSTER_ACTIVITIES[c.id] || []
    if (!acts.length) return ''
    return `<div style="background:white;border-radius:10px;padding:16px 18px;border:1.5px solid #D1FAE5;margin-bottom:14px">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
        <div style="width:36px;height:36px;border-radius:9px;background:${c.color}18;display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0">${c.icon}</div>
        <div>
          <div style="font-size:14px;font-weight:800;color:${c.color}">${c.label}</div>
          <div style="font-size:11px;color:#6B7280">Current level: ${c.pct}% · ${levelFor(c.pct).label}</div>
        </div>
      </div>
      ${acts.slice(0,2).map((act, i) => `
        <div style="display:flex;gap:10px;align-items:flex-start;margin-bottom:10px">
          <div style="min-width:26px;height:26px;border-radius:50%;background:#16A34A;color:white;font-size:12px;font-weight:900;display:flex;align-items:center;justify-content:center;flex-shrink:0">${i+1}</div>
          <div>
            <div style="font-size:13px;font-weight:700;color:#166534;margin-bottom:3px">${act.title}</div>
            <div style="font-size:13px;color:#374151;line-height:1.7">${act.desc}</div>
          </div>
        </div>`).join('')}
    </div>`
  }).join('')
}

function sigBlock() {
  return `<div class="sig-block">
    <div>
      <div class="sig-line">Reviewed by: ___________________________________</div>
      <div style="font-size:12px;color:#9CA3AF">Name &amp; Designation</div>
    </div>
    <div>
      <div class="sig-line">Signature: _____________________ &nbsp; Date: ___________</div>
      <div style="font-size:12px;color:#9CA3AF">Authorised NISH Clinician</div>
    </div>
  </div>`
}

function footerBlock() {
  return `<div style="border-top:2px solid #E5E7EB;margin-top:24px;padding-top:14px;display:flex;justify-content:space-between;align-items:center">
    <div style="display:flex;gap:6px">
      ${_nishSrc  ? `<img src="${_nishSrc}"  alt="NISH"  height="28" style="object-fit:contain"/>` : ''}
      ${_ncahtSrc ? `<img src="${_ncahtSrc}" alt="NCAHT" height="28" style="object-fit:contain"/>` : ''}
      ${_icmrSrc  ? `<img src="${_icmrSrc}"  alt="ICMR"  height="28" style="object-fit:contain"/>` : ''}
    </div>
    <div style="text-align:center;font-size:11px;color:#9CA3AF">
      National Institute of Speech &amp; Hearing · Thiruvananthapuram<br/>
      Vidyagni FSLP · Panchakosha SRT · NCF-FS 2022 · NEP 2020
    </div>
    <div style="font-size:11px;color:#9CA3AF;text-align:right">
      Generated: ${fmtDate(new Date())}<br/>ICMR-NCAHT Programme
    </div>
  </div>`
}

// ══════════════════════════════════════════════════════════════════════════════
// 1. PUBLIC SRT REPORT (from PublicSRTReports.jsx superadmin view)
//    Call: generatePublicSRTReport(record, session)
//    Replaces the inline printReport() in DetailModal
// ══════════════════════════════════════════════════════════════════════════════
export function generatePublicSRTReport(record, session) {
  const koshaScores = session?.koshaScores || {}
  const overall     = session?.overallScore ?? 0
  const responses   = session?.responses || {}
  const lv          = levelFor(overall)
  const today       = fmtDate(new Date())

  // info cards
  const infoCards = [
    ["Child's Name", record.name],
    ['Age', record.age + ' years old'],
    ['Disability / Condition', record.issue || 'None reported'],
    ['Assessment Date', fmtDate(session?.date)],
    ['Parent Email', record.parentEmail],
    ['Parent Phone', record.parentPhone],
    ['Skills Checked', session ? `${session.itemsAttempted ?? 0} out of 90` : '—'],
    ['Registered On', fmtDate(record.registeredAt)],
  ].map(([k,v]) => `<div class="info-card"><div class="label">${k}</div><div class="value">${v}</div></div>`).join('')

  const { rows: barRows, narratives } = koshaBarSection(record.name, koshaScores, responses)
  const itemRows    = itemTableSection(responses, koshaScores)
  const { clusterGrid, strengthsGrid, topNeeds } = clusterSection(responses)
  const activities  = homeActivitiesHTML(topNeeds)

  // recommendations
  const weak = KOSHAS.filter(k => { const p = koshaScores[k.id]??0; return p < 45 && p > 0 })
  const none = KOSHAS.filter(k => (koshaScores[k.id]??0) === 0)
  const recs = []
  if (overall >= 75) recs.push('Continue current developmental activities. Monitor progress every 3 months.')
  if (overall >= 45 && overall < 75) recs.push('Enrol in structured play-based early intervention. Review in 6 weeks.')
  if (overall < 45 && overall > 0)   recs.push('Recommend comprehensive clinical evaluation at NISH NCAHT. Priority referral.')
  weak.forEach(k => recs.push(`Focus intervention on ${k.full} (${k.icon}) — currently Emerging.`))
  if (none.length) recs.push(`${none.map(k=>k.full).join(', ')} domain(s) not assessed — follow-up required.`)
  if (record.issue) recs.push(`Consider disability-specific adaptations for ${record.issue}.`)
  const recsHTML = recs.map((r,i) => `<div style="display:flex;gap:12px;align-items:flex-start;margin-bottom:12px">
    <div style="min-width:28px;height:28px;border-radius:50%;background:${lv.color};color:white;font-size:13px;font-weight:900;display:flex;align-items:center;justify-content:center;flex-shrink:0">${i+1}</div>
    <p style="margin:0;font-size:14px;color:#1F2937;line-height:1.7;padding-top:3px">${r}</p>
  </div>`).join('')

  const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"/><title>SRT Report — ${record.name}</title>
${pageCSS()}</head><body>
${printBar(`School Readiness Report — ${record.name}`)}
<div class="page">
${nishHeader('School Readiness Report', today)}

<div class="section">
  <h2>👦 Child &amp; Parent Information</h2>
  <div class="info-grid">${infoCards}</div>
</div>

${overallScoreBlock(record.name, overall)}

<div class="section">
  <h2>📊 How ${record.name} Did in Each Area</h2>
  <p style="font-size:13px;color:#6B7280;margin-bottom:16px">We look at five areas of development. Each area has 18 activities. The percentage shows how many skills ${record.name} has achieved so far.</p>
  ${barRows}
</div>

<div class="section">
  <h2>📝 What We Observed</h2>
  ${narratives}
</div>

<div class="section page-break">
  <h2>💡 Our Recommendations</h2>
  <p style="font-size:13px;color:#6B7280;margin-bottom:16px">Based on ${record.name}'s results, here is what we suggest:</p>
  <div style="background:#FFFBEA;border:1.5px solid #FCD34D;border-radius:10px;padding:16px 20px">${recsHTML}</div>
</div>

<div style="background:#0F1A2E;border-radius:12px;padding:18px 22px;margin-bottom:28px;display:flex;align-items:center;gap:16px">
  <span style="font-size:32px">🏥</span>
  <div>
    <div style="font-weight:800;font-size:15px;color:white;margin-bottom:4px">Contact Parent for Follow-up</div>
    <div style="font-size:14px;color:rgba(255,255,255,.6)">📧 ${record.parentEmail} &nbsp;·&nbsp; 📱 ${record.parentPhone}</div>
  </div>
</div>

<div class="section page-break">
  <h2>🔬 Skill Area Breakdown</h2>
  <p style="font-size:13px;color:#6B7280;margin-bottom:16px">Skills from across all five areas are grouped into 13 specific ability clusters. This helps identify exactly which skills need support.</p>
  <div class="cluster-grid">${clusterGrid}</div>
  <div class="strengths-grid">${strengthsGrid}</div>
</div>

${topNeeds.length > 0 ? `<div class="section">
  <h2>🏠 Activities to Try at Home</h2>
  <p style="font-size:13px;color:#6B7280;margin-bottom:16px">These activities are designed for the areas where ${record.name} needs the most support. They use everyday materials and take only 10–15 minutes.</p>
  <div style="background:#F0FDF4;border-radius:12px;padding:18px 20px;border:1.5px solid #BBF7D0">${activities}</div>
</div>` : ''}

<div class="section page-break">
  <h2>📋 Detailed Skill-by-Skill Results</h2>
  <p style="font-size:13px;color:#6B7280;margin-bottom:20px">The full list of all 90 skills assessed, organised by developmental area.</p>
  ${itemRows}
</div>

<div class="disclaimer" style="margin-bottom:20px">
  <strong>⚠️ Important Notice:</strong> This report is based on a play-based developmental observation completed through the Vidyagni public platform.
  It is <strong>not a clinical diagnosis</strong>. All results should be reviewed by a qualified special educator, speech-language pathologist, or developmental specialist.
  For a full clinical evaluation, please contact NISH Thiruvananthapuram.
</div>
${sigBlock()}
${footerBlock()}
</div>
<div class="no-print" style="text-align:center;padding:20px">
  <button onclick="window.print()" style="padding:12px 32px;background:#0F1A2E;color:white;border:none;border-radius:9px;font-size:15px;font-weight:700;cursor:pointer">🖨️ Print / Save as PDF</button>
</div>
</body></html>`

  openReport(html)
}

// ══════════════════════════════════════════════════════════════════════════════
// 2. STAFF SRT — SINGLE SESSION
//    Call: generateStaffSRTReport(sess, child)
//    child: { name, dob, disabilities, iepGoals, color }
// ══════════════════════════════════════════════════════════════════════════════
export function generateStaffSRTReport(sess, child = {}) {
  const koshaScores = sess.koshaScores || {}
  const overall     = sess.overallScore ?? 0
  const responses   = sess.responses || {}
  const name        = child.name || '—'
  const today       = fmtDate(new Date())
  const sessDate    = fmtDate(sess.date || sess.createdAt)

  const infoCards = [
    ["Child's Name", name],
    ['Age', ageSafe(child.dob)],
    ['Disability / Profile', (child.disabilities||[]).join(', ') || 'None declared'],
    ['Assessment Date', sessDate],
    ['Conducted by', sess.conductedBy?.name || 'Staff'],
    ['IEP Goals', child.iepGoals || '—'],
    ['Items Rated', `${sess.itemsAttempted ?? 0} out of 90`],
    ['Framework', 'Panchakosha · NCF-FS 2022'],
  ].map(([k,v]) => `<div class="info-card"><div class="label">${k}</div><div class="value">${v}</div></div>`).join('')

  const { rows: barRows, narratives } = koshaBarSection(name, koshaScores, responses)
  const itemRows = itemTableSection(responses, koshaScores)
  const { clusterGrid, strengthsGrid, topNeeds } = clusterSection(responses)
  const activities = homeActivitiesHTML(topNeeds)

  const notesBlock = sess.sessionNotes
    ? `<div style="background:#EEF2FF;border-left:4px solid #4F46E5;border-radius:0 8px 8px 0;padding:12px 16px;margin-bottom:20px">
        <div style="font-size:11px;font-weight:800;color:#4338CA;letter-spacing:.5px;margin-bottom:4px">ASSESSOR NOTES</div>
        <div style="font-size:14px;color:#1E1B4B;line-height:1.7">${sess.sessionNotes}</div>
      </div>` : ''

  const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"/><title>SRT Report — ${name} — ${sessDate}</title>
${pageCSS()}</head><body>
${printBar(`SRT Report — ${name} — ${sessDate}`)}
<div class="page">
${nishHeader('School Readiness Assessment Report', today)}

<div class="section">
  <h2>👦 Child Profile</h2>
  <div class="info-grid">${infoCards}</div>
</div>

${notesBlock}
${overallScoreBlock(name, overall)}

<div class="section">
  <h2>📊 Panchakosha Domain Scores</h2>
  <p style="font-size:13px;color:#6B7280;margin-bottom:16px">Performance across five developmental domains. Each domain contains 18 assessed activities.</p>
  ${barRows}
</div>

<div class="section">
  <h2>📝 Domain Observations</h2>
  ${narratives}
</div>

<div class="section page-break">
  <h2>🔬 Skill Cluster Analysis</h2>
  <p style="font-size:13px;color:#6B7280;margin-bottom:16px">Skills are grouped into 13 clusters that cut across Panchakosha domains. Use this to identify specific intervention targets.</p>
  <div class="cluster-grid">${clusterGrid}</div>
  <div class="strengths-grid">${strengthsGrid}</div>
</div>

${topNeeds.length > 0 ? `<div class="section">
  <h2>🏠 Suggested Home &amp; Classroom Activities</h2>
  <p style="font-size:13px;color:#6B7280;margin-bottom:16px">Targeted activities for the clusters that need the most support. Each takes 10–15 minutes and uses everyday materials.</p>
  <div style="background:#F0FDF4;border-radius:12px;padding:18px 20px;border:1.5px solid #BBF7D0">${activities}</div>
</div>` : ''}

<div class="section page-break">
  <h2>📋 Full Item-by-Item Results</h2>
  <p style="font-size:13px;color:#6B7280;margin-bottom:20px">All 90 assessed skills with level, rubric descriptor, and assessor note.</p>
  ${itemRows}
</div>

<div class="disclaimer" style="margin-bottom:20px">
  <strong>⚠️ Important:</strong> This is a play-based developmental profile under the Panchakosha–NCF-FS 2022 framework.
  It does <em>not</em> constitute a clinical diagnosis. Interpret results with a qualified special educator or developmental specialist.
  Results reflect performance at the time of assessment only.
</div>

${footerBlock()}
</div>
<div class="no-print" style="text-align:center;padding:20px">
  <button onclick="window.print()" style="padding:12px 32px;background:#0F1A2E;color:white;border:none;border-radius:9px;font-size:15px;font-weight:700;cursor:pointer">🖨️ Print / Save as PDF</button>
</div>
</body></html>`

  openReport(html)
}

// ══════════════════════════════════════════════════════════════════════════════
// 3. STAFF SRT — CONSOLIDATED (all sessions)
//    Call: generateStaffSRTConsolidated(sessions, child)
// ══════════════════════════════════════════════════════════════════════════════
export function generateStaffSRTConsolidated(sessions, child = {}) {
  const name  = child.name || '—'
  const today = fmtDate(new Date())
  const sorted = [...sessions].sort((a,b) => new Date(a.date||a.createdAt) - new Date(b.date||b.createdAt))

  // trajectory table
  const trajRows = sorted.map((sess, i) => {
    const d    = fmtDate(sess.date||sess.createdAt)
    const ovr  = sess.overallScore ?? 0
    const lv   = levelFor(ovr)
    const prev = i > 0 ? (sorted[i-1].overallScore ?? 0) : null
    const delta = prev !== null ? ovr - prev : null
    const dc = delta === null ? '' : delta > 0 ? '#16A34A' : delta < 0 ? '#DC2626' : '#6B7280'
    const kCells = KOSHAS.map(k => {
      const p = sess.koshaScores?.[k.id] ?? 0
      const c = p >= 75 ? '#16A34A' : p >= 45 ? '#D97706' : p > 0 ? '#DC2626' : '#9CA3AF'
      return `<td style="padding:6px 10px;text-align:center;font-weight:700;color:${c};font-size:13px">${p > 0 ? p+'%' : '—'}</td>`
    }).join('')
    return `<tr style="border-bottom:1px solid #E5E7EB${i===sorted.length-1?';background:#F0FDF4':''}">
      <td style="padding:7px 10px;font-size:12px;color:#6B7280">${i+1}</td>
      <td style="padding:7px 10px;font-size:13px;font-weight:600">${d}</td>
      ${kCells}
      <td style="padding:7px 10px;text-align:center">
        <span style="font-weight:900;font-size:16px;color:${lv.color}">${ovr > 0 ? ovr+'%' : '—'}</span>
        ${delta!==null?`<br/><span style="font-size:11px;color:${dc}">${delta>0?'+':''}${delta}%</span>`:''}
      </td>
      <td style="padding:7px 10px;font-size:12px;color:#9CA3AF">${sess.itemsAttempted??0} items</td>
    </tr>`
  }).join('')

  // per-session detail
  const sessionSections = sorted.map((sess, i) => {
    const sessDate    = fmtDate(sess.date||sess.createdAt)
    const koshaScores = sess.koshaScores || {}
    const overall     = sess.overallScore ?? 0
    const lv          = levelFor(overall)
    const responses   = sess.responses || {}

    const { rows: barRows, narratives } = koshaBarSection(name, koshaScores, responses)
    const itemRows = itemTableSection(responses, koshaScores)
    const { clusterGrid, strengthsGrid, topNeeds } = clusterSection(responses)
    const activities = homeActivitiesHTML(topNeeds)

    const notesBlock = sess.sessionNotes
      ? `<div style="background:#EEF2FF;border-left:4px solid #4F46E5;border-radius:0 8px 8px 0;padding:10px 14px;margin-bottom:16px">
          <div style="font-size:11px;font-weight:800;color:#4338CA;letter-spacing:.5px;margin-bottom:3px">ASSESSOR NOTES</div>
          <div style="font-size:13px;color:#1E1B4B;line-height:1.7">${sess.sessionNotes}</div>
        </div>` : ''

    return `<div class="${i>0?'page-break section':'section'}" style="border:2px solid #E5E7EB;border-radius:12px;overflow:hidden;margin-bottom:28px">
      <div style="background:#0F1A2E;padding:14px 20px;display:flex;justify-content:space-between;align-items:center">
        <div>
          <span style="font-weight:900;font-size:16px;color:white">Session ${i+1}</span>
          <span style="font-size:13px;color:rgba(255,255,255,.5);margin-left:10px">${sessDate}</span>
          <span style="font-size:12px;color:rgba(255,255,255,.4);margin-left:10px">by ${sess.conductedBy?.name||'Staff'} · ${sess.itemsAttempted??0} items</span>
        </div>
        <div style="text-align:right">
          <div style="font-size:28px;font-weight:900;color:${lv.color};line-height:1">${overall}%</div>
          <div style="background:${lv.bg};color:${lv.color};font-size:10px;font-weight:700;padding:2px 9px;border-radius:5px;display:inline-block;margin-top:3px">${lv.label}</div>
        </div>
      </div>
      <div style="padding:20px 22px">
        ${notesBlock}
        <h2>Domain Scores</h2>
        ${barRows}
        <h2 style="margin-top:20px">Domain Observations</h2>
        ${narratives}
        <h2 style="margin-top:20px">Skill Cluster Analysis</h2>
        <div class="cluster-grid">${clusterGrid}</div>
        <div class="strengths-grid">${strengthsGrid}</div>
        ${topNeeds.length > 0 ? `<h2 style="margin-top:20px">Suggested Activities</h2>
          <div style="background:#F0FDF4;border-radius:12px;padding:18px 20px;border:1.5px solid #BBF7D0">${activities}</div>` : ''}
        <h2 style="margin-top:20px">Full Item Results</h2>
        ${itemRows}
      </div>
    </div>`
  }).join('')

  const from = fmtDate(sorted[0].date||sorted[0].createdAt)
  const to   = fmtDate(sorted[sorted.length-1].date||sorted[sorted.length-1].createdAt)

  const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"/><title>SRT Progress History — ${name}</title>
${pageCSS()}</head><body>
${printBar(`SRT Progress History — ${name} — ${sorted.length} sessions`)}
<div class="page">
${nishHeader('SRT Progress History Report', today)}

<div class="section">
  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
    <div>
      <h2 style="border:none;margin:0;padding:0">${name}</h2>
      <div style="font-size:13px;color:#6B7280;margin-top:3px">${(child.disabilities||[]).join(', ')||'No disability noted'} · Age: ${ageSafe(child.dob)}</div>
    </div>
    <div style="text-align:right;font-size:12px;color:#9CA3AF">
      <div>${sorted.length} session${sorted.length!==1?'s':''} · ${from} → ${to}</div>
    </div>
  </div>
  <h2>📈 Progress Trajectory</h2>
  <table style="width:100%;border-collapse:collapse;border:1.5px solid #E5E7EB;border-radius:8px;overflow:hidden">
    <thead>
      <tr style="background:#0F1A2E;color:white">
        <th style="padding:8px 10px;text-align:left;font-size:11px">#</th>
        <th style="padding:8px 10px;text-align:left;font-size:11px">Date</th>
        ${KOSHAS.map(k=>`<th style="padding:8px 10px;text-align:center;font-size:11px">${k.icon} ${k.label}</th>`).join('')}
        <th style="padding:8px 10px;text-align:center;font-size:11px">Overall</th>
        <th style="padding:8px 10px;text-align:left;font-size:11px">Items</th>
      </tr>
    </thead>
    <tbody>${trajRows}</tbody>
  </table>
  <div style="font-size:11px;color:#9CA3AF;margin-top:6px">
    Green ≥75% (Achieved) · Amber 45–74% (Developing) · Red &lt;45% (Emerging) · Δ = change from previous session
  </div>
</div>

<div class="disclaimer" style="margin-bottom:24px">
  <strong>Important:</strong> This is a play-based developmental profile. It does not constitute a clinical diagnosis.
  Results reflect performance at the time of each assessment.
</div>

<div style="font-size:18px;font-weight:800;color:#0F1A2E;margin-bottom:16px;padding-bottom:6px;border-bottom:2px solid #E5E7EB">
  Detailed Session Records</div>
${sessionSections}


${footerBlock()}
</div>
<div class="no-print" style="text-align:center;padding:20px">
  <button onclick="window.print()" style="padding:12px 32px;background:#0F1A2E;color:white;border:none;border-radius:9px;font-size:15px;font-weight:700;cursor:pointer">🖨️ Print / Save as PDF</button>
</div>
</body></html>`

  openReport(html)
}

// ══════════════════════════════════════════════════════════════════════════════
// 4. QUICK SCREENER REPORT
//    Call: generateQSReport(result, child)
//    result: { ratings, domainScores, overallScore, recommendation, flags, notes, date }
// ══════════════════════════════════════════════════════════════════════════════

const QS_DOMAIN_SIGNALS = {
  annamaya:    ['an1-grip','an1-figure','an1-trace','an1-posture','an1-shapes','an2-jump','an2-balance','an2-rhythm','an2-throw','an2-engage'],
  pranamaya:   ['pr1-recog','pr1-label','pr1-empathy','pr1-pretend','pr1-express','pr2-turns','pr2-share','pr2-wait','pr2-rules','pr2-repair'],
  manomaya:    ['mn1-sort','mn1-pattern','mn1-oddone','mn1-focus','mn1-explain','mn2-recall','mn2-3step','mn2-retell','mn2-sequence','mn2-attend'],
  vijnanamaya: ['vj1-count','vj1-bigger','vj1-letters','vj1-sound','vj1-1to1','vj2-describe','vj2-sentence','vj2-vocab','vj2-name','vj2-ordinal'],
  anandamaya:  ['aa1-intent','aa1-explains','aa1-colour','aa1-flag','aa1-engage','aa2-animal','aa2-move','aa2-music','aa2-invent','aa2-story'],
}
const QS_SIGNAL_LABELS = {
  'an1-grip':'Pencil/crayon grip','an1-figure':'Person drawing complexity','an1-trace':'Follows a dotted path','an1-posture':'Appropriate seated posture','an1-shapes':'Attempts or copies shapes',
  'an2-jump':'Jumps with both feet & lands','an2-balance':'Balances on one foot','an2-rhythm':'Copies clap rhythm','an2-throw':'Throws with directional aim','an2-engage':'Engages willingly in physical activity',
  'pr1-recog':'Identifies 2+ basic emotions','pr1-label':'Labels emotion (word/sign)','pr1-empathy':'Shows empathic response','pr1-pretend':'Puppet expresses emotion','pr1-express':'Expresses own feeling verbally',
  'pr2-turns':'Waits for turn (ball roll)','pr2-share':'Shares without resistance','pr2-wait':'Waits in line calmly','pr2-rules':'Follows 2-step game instruction','pr2-repair':'Responds cooperatively',
  'mn1-sort':'Sorts into 2 groups correctly','mn1-pattern':'Extends AB colour pattern','mn1-oddone':'Identifies the odd one out','mn1-focus':'Sustains focus across sub-tasks','mn1-explain':'Gives a reason for choice',
  'mn2-recall':'Recalls 2+ of 3 hidden objects','mn2-3step':'Follows all 3-step instruction','mn2-retell':'Retells story in correct order','mn2-sequence':'Uses sequence words','mn2-attend':'Listens to story without prompting',
  'vj1-count':'Counts 4 objects correctly','vj1-bigger':'Identifies bigger of two numbers','vj1-letters':'Recognises 3+ printed letters','vj1-sound':'Identifies first sound of SUN','vj1-1to1':'1-to-1 counting correspondence',
  'vj2-describe':'Describes full scene (not just labels)','vj2-sentence':'Forms a grammatical sentence','vj2-vocab':'Uses varied vocabulary (5+ words)','vj2-name':'Writes or spells own name','vj2-ordinal':'Uses position/sequence language',
  'aa1-intent':'Draws with clear creative intent','aa1-explains':'Describes what they made','aa1-colour':'Chooses colours deliberately','aa1-flag':'Creates a distinct flag design','aa1-engage':'Shows visible joy or absorption',
  'aa2-animal':'Names animal + shares a fact','aa2-move':'Imitates the animal\'s movement','aa2-music':'Joins in with music','aa2-invent':'Describes an imaginary invention','aa2-story':'Adds narrative to animal',
}
const QS_ACTIVITY_FOR = {
  'an1-grip':'Drawing Challenge','an1-figure':'Drawing Challenge','an1-trace':'Drawing Challenge','an1-posture':'Drawing Challenge','an1-shapes':'Drawing Challenge',
  'an2-jump':'Move Like Me','an2-balance':'Move Like Me','an2-rhythm':'Move Like Me','an2-throw':'Move Like Me','an2-engage':'Move Like Me',
  'pr1-recog':'Feelings Puppet Show','pr1-label':'Feelings Puppet Show','pr1-empathy':'Feelings Puppet Show','pr1-pretend':'Feelings Puppet Show','pr1-express':'Feelings Puppet Show',
  'pr2-turns':'Sharing Game','pr2-share':'Sharing Game','pr2-wait':'Sharing Game','pr2-rules':'Sharing Game','pr2-repair':'Sharing Game',
  'mn1-sort':'Sorting Tray','mn1-pattern':'Sorting Tray','mn1-oddone':'Sorting Tray','mn1-focus':'Sorting Tray','mn1-explain':'Sorting Tray',
  'mn2-recall':'Memory Sequence','mn2-3step':'Memory Sequence','mn2-retell':'Memory Sequence','mn2-sequence':'Memory Sequence','mn2-attend':'Memory Sequence',
  'vj1-count':'Number & Letter Hunt','vj1-bigger':'Number & Letter Hunt','vj1-letters':'Number & Letter Hunt','vj1-sound':'Number & Letter Hunt','vj1-1to1':'Number & Letter Hunt',
  'vj2-describe':'Story Picture','vj2-sentence':'Story Picture','vj2-vocab':'Story Picture','vj2-name':'Story Picture','vj2-ordinal':'Story Picture',
  'aa1-intent':'Creation Station','aa1-explains':'Creation Station','aa1-colour':'Creation Station','aa1-flag':'Creation Station','aa1-engage':'Creation Station',
  'aa2-animal':'Imagination Round','aa2-move':'Imagination Round','aa2-music':'Imagination Round','aa2-invent':'Imagination Round','aa2-story':'Imagination Round',
}

export function generateQSReport(result, child = {}) {
  const ratings      = result.ratings || {}
  const domainScores = result.domainScores || {}
  const overall      = result.overallScore ?? 0
  const rec          = result.recommendation || 'monitor'
  const name         = child.name || '—'
  const today        = fmtDate(new Date())
  const screenDate   = fmtDate(result.date || result.createdAt || new Date())

  const REC_CFG = {
    'no-concerns':{ icon:'✅', label:'No Concerns',       color:'#16A34A', bg:'#DCFCE7',
      text:`${name} shows age-appropriate development across all Panchakosha domains. No immediate intervention required. The full 90-item SRT can still be conducted anytime for a deeper, cluster-level profile, but it is not required based on these results. Re-screen in 6 months.` },
    'monitor':    { icon:'⚠️', label:'Monitor & Support',  color:'#D97706', bg:'#FEF3C7',
      text:`Emerging skills observed in one or more domains. Provide targeted classroom support and re-screen in 8–10 weeks. If no improvement, conduct the full 90-item SRT.` },
    'full-srt':   { icon:'🔴', label:'Full SRT Recommended',color:'#DC2626', bg:'#FEE2E2',
      text:`Significantly delayed skills observed in 2+ Panchakosha domains. Conduct the full 90-item Panchakosha SRT and share results with a qualified special educator for an individualised plan.` },
  }
  const rc = REC_CFG[rec] || REC_CFG['monitor']

  const SIGNAL_LEVELS = {
    strong:  { label:'Strong',   color:'#16A34A', bg:'#DCFCE7', desc:'Clear, independent' },
    present: { label:'Present',  color:'#2563EB', bg:'#EFF6FF', desc:'With minor support' },
    emerging:{ label:'Emerging', color:'#D97706', bg:'#FEF3C7', desc:'Attempted, inconsistent' },
    absent:  { label:'Absent',   color:'#DC2626', bg:'#FEE2E2', desc:'Not observed / refused' },
  }

  const lv = levelFor(overall)

  const infoCards = [
    ["Child's Name", name],
    ['Age', ageSafe(child.dob)],
    ['Disability / Profile', (child.disabilities||[]).join(', ') || 'None declared'],
    ['Screening Date', screenDate],
    ['Screened by', result.conductedBy?.name || 'Staff'],
    ['Signals Rated', `${Object.values(ratings).filter(Boolean).length} of 50`],
    ['Instrument', 'Panchakosha Quick Screener · 10 activities'],
    ['IEP Goals', child.iepGoals || '—'],
  ].map(([k,v]) => `<div class="info-card"><div class="label">${k}</div><div class="value">${v}</div></div>`).join('')

  const domainOverview = KOSHAS.map(k => {
    const flag        = result.flags?.[k.id] || 'amber'
    const notAssessed  = flag === 'none' || domainScores[k.id] == null
    const sc           = notAssessed ? 0 : (domainScores[k.id] ?? 0)
    const fc   = { green:'#16A34A', amber:'#D97706', red:'#DC2626' }[flag] || '#9CA3AF'
    const fb   = { green:'#DCFCE7', amber:'#FEF3C7', red:'#FEE2E2' }[flag] || '#F3F4F6'
    const fl   = { green:'✅ No concerns', amber:'⚠️ Monitor', red:'🔴 Refer for SRT' }[flag] || '— Not Assessed'
    return `<div style="background:${fb};border-radius:10px;padding:12px;border:1.5px solid ${fc}33;text-align:center">
      <div style="font-size:22px;margin-bottom:4px">${k.icon}</div>
      <div style="font-size:20px;font-weight:900;color:${fc}">${notAssessed ? '—' : sc+'%'}</div>
      <div style="font-size:11px;font-weight:700;color:${fc};margin-top:2px">${k.label}</div>
      <div style="font-size:10px;color:${fc};margin-top:4px">${fl}</div>
      <div style="height:6px;background:rgba(0,0,0,.08);border-radius:3px;margin-top:8px;overflow:hidden">
        <div style="width:${sc}%;height:6px;background:${fc};border-radius:3px"></div>
      </div>
    </div>`
  }).join('')

  const domainSignalSections = KOSHAS.map(k => {
    const flag        = result.flags?.[k.id] || 'amber'
    const notAssessed  = flag === 'none' || domainScores[k.id] == null
    const sc           = notAssessed ? 0 : (domainScores[k.id] ?? 0)
    const fc    = { green:'#16A34A', amber:'#D97706', red:'#DC2626' }[flag] || '#9CA3AF'
    const fb    = { green:'#DCFCE7', amber:'#FEF3C7', red:'#FEE2E2' }[flag] || '#F3F4F6'
    const fl    = { green:'✅ No concerns', amber:'⚠️ Monitor', red:'🔴 Refer for SRT' }[flag] || '— Not Assessed'
    const sigs  = QS_DOMAIN_SIGNALS[k.id] || []
    const rows  = sigs.map(sig => {
      const lv  = ratings[sig]
      const lvd = lv ? SIGNAL_LEVELS[lv] : null
      return `<tr style="border-bottom:1px solid #E5E7EB">
        <td style="padding:7px 12px;font-size:12px;color:#6B7280;width:130px">${QS_ACTIVITY_FOR[sig]||''}</td>
        <td style="padding:7px 12px;font-size:13px;color:#111827">${QS_SIGNAL_LABELS[sig]||sig}</td>
        <td style="padding:7px 12px;text-align:center;width:100px">
          ${lvd ? `<span style="display:inline-block;background:${lvd.bg};color:${lvd.color};font-size:11px;font-weight:800;padding:3px 10px;border-radius:5px;border:1px solid ${lvd.color}55">${lvd.label}</span>`
                : '<span style="font-size:11px;color:#9CA3AF">—</span>'}
        </td>
        <td style="padding:7px 12px;font-size:12px;color:#6B7280">${lvd?.desc||'Not rated'}</td>
      </tr>`
    }).join('')
    return `<div style="margin-bottom:20px;page-break-inside:avoid">
      <div style="background:${k.color};color:white;padding:10px 16px;border-radius:8px 8px 0 0;display:flex;justify-content:space-between;align-items:center">
        <span style="font-size:15px;font-weight:800">${k.icon} ${k.full}</span>
        <div style="display:flex;align-items:center;gap:10px">
          <span style="font-size:14px;font-weight:700">${notAssessed ? '—' : sc+'%'}</span>
          <span style="background:rgba(255,255,255,.2);font-size:11px;font-weight:700;padding:2px 10px;border-radius:14px">${fl}</span>
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;border:1px solid #E5E7EB;border-top:none">
        <thead><tr style="background:#F8FAFC">
          <th style="padding:7px 12px;text-align:left;font-size:11px;color:#6B7280;font-weight:700;text-transform:uppercase;width:130px">Activity</th>
          <th style="padding:7px 12px;text-align:left;font-size:11px;color:#6B7280;font-weight:700;text-transform:uppercase">Signal Observed</th>
          <th style="padding:7px 12px;text-align:center;font-size:11px;color:#6B7280;font-weight:700;text-transform:uppercase;width:100px">Rating</th>
          <th style="padding:7px 12px;text-align:left;font-size:11px;color:#6B7280;font-weight:700;text-transform:uppercase">Descriptor</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`
  }).join('')

  const notesBlock = result.notes
    ? `<div style="background:#EEF2FF;border-left:4px solid #4F46E5;border-radius:0 8px 8px 0;padding:12px 16px;margin-bottom:20px">
        <div style="font-size:11px;font-weight:800;color:#4338CA;letter-spacing:.5px;margin-bottom:4px">SCREENER NOTES</div>
        <div style="font-size:14px;color:#1E1B4B;line-height:1.7">${result.notes}</div>
      </div>` : ''

  const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"/><title>Quick Screener — ${name} — ${screenDate}</title>
${pageCSS()}</head><body>
${printBar(`Quick Screener Report — ${name} — ${screenDate}`)}
<div class="page">
${nishHeader('Quick Screener Report', today)}

<div class="section">
  <h2>👦 Child Profile</h2>
  <div class="info-grid">${infoCards}</div>
</div>

${notesBlock}

<!-- Screening Decision -->
<div style="background:${rc.bg};border:2px solid ${rc.color}44;border-radius:12px;padding:20px 24px;display:flex;align-items:flex-start;gap:20px;margin-bottom:22px">
  <div style="font-size:48px;flex-shrink:0">${rc.icon}</div>
  <div style="flex:1">
    <div style="font-size:22px;font-weight:900;color:${rc.color};margin-bottom:6px">${rc.label}</div>
    <p style="font-size:14px;color:#374151;line-height:1.75">${rc.text}</p>
  </div>
  <div style="text-align:center;flex-shrink:0">
    <div style="font-size:12px;color:#6B7280;margin-bottom:4px">OVERALL</div>
    <div style="font-size:42px;font-weight:900;color:${lv.color};line-height:1">${overall}%</div>
    <div style="background:${lv.bg};color:${lv.color};font-size:12px;font-weight:700;padding:3px 12px;border-radius:6px;margin-top:4px;display:inline-block">${lv.label}</div>
  </div>
</div>

<div class="section">
  <h2>📊 Domain Overview</h2>
  <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-bottom:16px">${domainOverview}</div>
</div>

<div class="section">
  <h2>🔬 Skill Cluster Analysis</h2>
  ${qsClusterPreviewHTML(domainScores, result.flags)}
</div>

<div class="section page-break">
  <h2>📋 All 50 Signal Ratings</h2>
  <p style="font-size:13px;color:#6B7280;margin-bottom:16px">Each domain was observed through 2 activities covering 10 signals. Ratings: Strong (clear, independent) · Present (with minor support) · Emerging (inconsistent) · Absent (not observed).</p>
  ${domainSignalSections}
</div>

<div class="disclaimer" style="margin-bottom:20px">
  <strong>⚠️ Important:</strong> This quick screener captures approximate developmental signals through 10 structured activities (~15–20 minutes).
  It is <em>not a diagnostic tool</em>. A "Full SRT Recommended" result should be followed by the 90-item Panchakosha SRT conducted by a qualified educator or developmental specialist.
</div>

${footerBlock()}
</div>
<div class="no-print" style="text-align:center;padding:20px">
  <button onclick="window.print()" style="padding:12px 32px;background:#0F1A2E;color:white;border:none;border-radius:9px;font-size:15px;font-weight:700;cursor:pointer">🖨️ Print / Save as PDF</button>
</div>
</body></html>`

  openReport(html)
}